import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight } from 'lucide-react';

import { storeSelectedBranch } from '@/lib/branchTheme';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BranchSelectPage = () => {
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/branches`);
        setBranches(data?.branches || []);
      } catch (e) {
        setBranches([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-white relative overflow-hidden" data-testid="branch-select-page">
      <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        {/* Header */}
        <div className="max-w-2xl">
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.25em] text-teal-600">
            Invicta Management Platform
          </span>
          <h1
            className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.05]"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Eine Plattform.<br />
            <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg,#0D9488,#7C3AED)' }}>
              Für jede Branche.
            </span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Wählen Sie Ihren Bereich — Buchung, Kundenverwaltung und Zahlungen
            an einem Ort, individuell angepasst an Ihre Branche.
          </p>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-24">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-400" />
          </div>
        ) : (
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="branch-grid">
            {branches.map((b, i) => (
              <button
                key={b.key}
                type="button"
                data-testid={`branch-card-${b.key}`}
                onClick={() => { storeSelectedBranch(b); navigate(`/login?branch=${b.key}`); }}
                className="group text-left rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-teal-400/60"
              >
                {/* Accent top bar */}
                <div className="h-1.5 w-full" style={{ background: b.accent_gradient }} />

                <div className="p-6">
                  <div className="h-44 flex items-center justify-center">
                    <img
                      src={b.logo_url}
                      alt={b.name}
                      loading="eager"
                      decoding="async"
                      className="max-h-44 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.04]"
                    />
                  </div>

                  <p className="mt-2 text-sm text-slate-500 leading-relaxed min-h-[40px] text-center">
                    {b.description}
                  </p>

                  <div
                    className="mt-5 flex items-center justify-center gap-2 text-sm font-semibold transition-colors"
                    style={{ color: b.accent_color }}
                  >
                    Zur Anmeldung
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="mt-16 text-center text-sm text-slate-500">
          Bereits ein Konto?{' '}
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-teal-600 hover:text-teal-700 font-semibold"
            data-testid="branch-login-link"
          >
            Direkt anmelden
          </button>
        </div>
      </div>
    </div>
  );
};

export default BranchSelectPage;
